<template>
  <Modal
    class="update-competition-resources-modal"
    :title="`Update competition resources`"
    :visible="true"
    no-close-btn
    @close="onModalClose"
  >
    <Spinner v-if="isLoading" />
    <div
      v-else
      class="modal-content"
    >
      <p>Input the number of workers per event for the <b>{{ competition?.competitionName }}</b> competition</p>
      <div class="input-root">
        <span>Workers per event</span>
        <NumberInput
          :model-value="workersPerEvent"
          @update:modelValue="updateParameter"
          :max="10"
        />
      </div>
    </div>
    <div class="modal-footer">
      <Button
        @click="onModalClose"
        variant="tertiary"
      >
        Cancel
      </Button>
      <Button
        @click="updateResources"
        variant="danger"
        :disabled="isLoading || !submitEnabled"
      >
        Update resources
      </Button>
    </div>
  </Modal>
</template>

<script>
import {
  computed,
  onMounted,
  ref,
} from 'vue';
import { useStore } from 'vuex';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import NumberInput from '@/components/common/NumberInput';
import Spinner from '@/components/common/Spinner';
import { getWorkersPerCompetition } from '@/services/api';

export default {
  components: {
    Button,
    Modal,
    NumberInput,
    Spinner,
  },
  props: {
    competition: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: {
    onClose: {},
    onSubmit: {
      type: Object,
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const originalWorkersPerEvent = ref(1);
    const workersPerEvent = ref(1);
    const isLoading = ref(false);
    const submitEnabled = computed(() => workersPerEvent.value !== originalWorkersPerEvent.value);

    const updateResources = () => {
      isLoading.value = true;
      store.dispatch('updateWorkersPerCompetition', {
        competitionId: props.competition?.competitionId,
        data: {
          workersPerEvent: workersPerEvent.value,
        },
      }).then(() => {
        emit('onClose');
      }).finally(() => {
        isLoading.value = false;
      });
    };
    const resetResources = () => {
      workersPerEvent.value = 1;
      originalWorkersPerEvent.value = 1;
    };

    const onModalClose = () => {
      emit('onClose');
      resetResources();
    };

    const updateParameter = (value) => {
      workersPerEvent.value = value;
    };

    const loadWorkersPerCompetition = () => {
      if (props.competition?.competitionId) {
        isLoading.value = true;
        getWorkersPerCompetition(props.competition?.competitionId)
          .then((response) => {
            if (!response) {
              return;
            }
            originalWorkersPerEvent.value = response?.workersPerEvent;
            workersPerEvent.value = response?.workersPerEvent;
          }).finally(() => {
            isLoading.value = false;
          });
      }
    };

    onMounted(() => {
      if (props.competition?.competitionId) {
        loadWorkersPerCompetition();
      }
    });

    return {
      workersPerEvent,
      originalWorkersPerEvent,
      isLoading,
      submitEnabled,
      updateResources,
      onModalClose,
      updateParameter,
    };
  },
};
</script>

<style lang="scss">
.update-competition-resources-modal {
  .modal__dialog {
    max-width: 350px;
  }
  .modal__header {
    height: auto;
    padding: 16px;
    border-bottom: 1px solid $gray400;
    .modal__title {
      padding: 0;
    }
  }
  .modal-content {
    padding: 16px;
    .input-root {
      margin-top: 16px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      .table-number-input {
        max-width: 100px;
      }
      span {
        font-size: 10px;
        color: $gray800;
        text-transform: uppercase;
      }
    }
  }
  .modal-footer {
    padding: 0 16px 16px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}
</style>
